import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Button,
    FormControl,
} from '@mui/material'
import React from 'react'
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form'
import PasswordField from './PasswordField'
import { CustomerEntity } from 'types/CustomerEntity'
import CustomerSelect from './CustomerSelect'
import { UserCategorySelect, UserCategoryType } from './UserCategorySelect'

export type NewUserForm = FieldValues & {
    name: string
    customer: CustomerEntity | null
    email: string
    password: string
    userCategory: UserCategoryType
}

function NewUserDialog({
    open,
    onClose,
    onSubmit,
}: {
    open: boolean
    onClose: () => void
    onSubmit: (val: NewUserForm) => Promise<void>
}) {
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue, // For setting form field values programmatically
        reset,
        watch,
    } = useForm<NewUserForm>()

    const handleFormSubmit: SubmitHandler<NewUserForm> = (data) => {
        onSubmit(data)
            .then(() => reset()) // Reset form after submission
            .catch((err) => console.error(err))
    }

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Skapa ny användare</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Fyll i namn, e-postadress och lösenord för att skapa en ny användare.
                </DialogContentText>
                <form onSubmit={(event) => void handleSubmit(handleFormSubmit)(event)}>
                    {/* Name Field */}
                    <TextField
                        autoFocus
                        margin='dense'
                        label='Namn'
                        type='text'
                        fullWidth
                        {...register('name', { required: 'Namn krävs' })}
                        error={!!errors.name}
                        helperText={errors.name ? errors.name.message : ''}
                    />

                    <FormControl fullWidth margin='dense'>
                        <CustomerSelect
                            label='Kund'
                            error={!!errors.customer}
                            helperText={errors.customer?.message}
                            onChange={(customer) => setValue('customer', customer)}
                        />
                    </FormControl>

                    <FormControl fullWidth margin='dense'>
                        <UserCategorySelect
                            onChange={(userCategory) => setValue('userCategory', userCategory)}
                            value={watch('userCategory')}
                        />
                    </FormControl>

                    {/* Email Field */}
                    <TextField
                        margin='dense'
                        label='E-post'
                        type='email'
                        fullWidth
                        {...register('email', {
                            required: 'E-post krävs',
                            pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: 'Ogiltig e-postadress',
                            },
                        })}
                        error={!!errors.email}
                        helperText={errors.email ? errors.email.message : ''}
                    />

                    {/* Password Field with Toggle and Random Password */}
                    <PasswordField
                        label='Lösenord'
                        register={register('password', {
                            required: 'Lösenord krävs',
                            minLength: {
                                value: 10,
                                message: 'Lösenord måste vara minst 10 tecken långt',
                            },
                            validate: (value) =>
                                [/[a-z]/, /[A-Z]/, /\d/].every((pattern) => pattern.test(value)) ||
                                'Lösenordet måste innehålla minst en liten bokstav, en stor bokstav och en siffra',
                        })}
                        error={!!errors.password}
                        helperText={errors.password ? errors.password.message : ''}
                        showGeneratePassword={true}
                        onChange={(e) => setValue('password', e.target.value)}
                    />

                    <DialogActions>
                        <Button onClick={onClose} color='primary'>
                            Avbryt
                        </Button>
                        <Button type='submit' color='primary' variant='contained'>
                            Skapa
                        </Button>
                    </DialogActions>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default NewUserDialog
